import React, { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

export type ExternalChannelType = 'Email' | 'Discord' | 'WhatsApp';

interface ExternalChannelTypeSelectProps {
  value: ExternalChannelType;
  onChange: (channelType: ExternalChannelType) => void;
  disabled?: boolean;
}

const DiscordIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.956 2.419-2.157 2.419zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.946 2.419-2.157 2.419z" />
  </svg>
);

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.85 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const EXTERNAL_CHANNEL_TYPE_LABELS: Record<ExternalChannelType, string> = {
  Email: 'E-mail',
  Discord: 'Discord',
  WhatsApp: 'WhatsApp',
};

const CHANNEL_OPTIONS: Array<{
  value: ExternalChannelType;
  label: string;
  disabled: boolean;
}> = [
  { value: 'Email', label: EXTERNAL_CHANNEL_TYPE_LABELS.Email, disabled: false },
  { value: 'Discord', label: EXTERNAL_CHANNEL_TYPE_LABELS.Discord, disabled: false },
  { value: 'WhatsApp', label: EXTERNAL_CHANNEL_TYPE_LABELS.WhatsApp, disabled: true },
];

export const ExternalChannelTypeIcon: React.FC<{
  channelType: ExternalChannelType;
  className?: string;
}> = ({ channelType, className = 'h-4 w-4' }) => {
  switch (channelType)
  {
    case 'Discord':
      return <DiscordIcon className={`${className} text-[#5865F2]`} />;
    case 'WhatsApp':
      return <WhatsAppIcon className={`${className} text-[#25D366]`} />;
    case 'Email':
    default:
      return <EnvelopeIcon className={`${className} text-amber-500`} />;
  }
};

export const ExternalChannelTypeSelect: React.FC<ExternalChannelTypeSelectProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const selectedLabel = EXTERNAL_CHANNEL_TYPE_LABELS[value];

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className="relative">
        <Listbox.Button
          className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-left text-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
        >
          <span className="flex items-center gap-2 min-w-0">
            <ExternalChannelTypeIcon channelType={value} className="h-4 w-4 flex-shrink-0" />
            <span className="block truncate font-medium text-gray-900">{selectedLabel}</span>
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
            {CHANNEL_OPTIONS.map((option) => (
              <Listbox.Option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                as={Fragment}
              >
                {({ active, selected, disabled: optionDisabled }) => (
                  <li
                    className={`relative flex select-none items-center gap-2 px-3 py-2 ${
                      optionDisabled
                        ? 'cursor-not-allowed opacity-50'
                        : selected || active
                          ? 'cursor-pointer bg-blue-50'
                          : 'cursor-pointer'
                    }`}
                  >
                    <ExternalChannelTypeIcon
                      channelType={option.value}
                      className="h-4 w-4 flex-shrink-0"
                    />
                    <span
                      className={`flex-1 truncate text-gray-900 ${
                        selected ? 'font-semibold' : 'font-normal'
                      }`}
                    >
                      {option.label}
                      {optionDisabled ? ' (em breve)' : ''}
                    </span>
                    {selected && !optionDisabled && (
                      <CheckIcon className="h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
                    )}
                  </li>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};
